import { useEffect, useState, useCallback } from "react";
import { UserDetail } from "../components/UserDetail";
import { UserList } from "../components/UserList";
import { getAllUsers } from "../api/adminUserApi";
import { getListingsByUser } from "../api/adminListingApi";
import { getUsersConversations } from "../api/adminChatApi";
import { adminApi } from "../utils/admin-api-axios";
import { useLocation, useNavigate } from "react-router-dom";

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [reports, setReports] = useState([]);

  const [userLoading, setUserLoading] = useState(true);
  const [listingLoading, setListingLoading] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const selectedUserIdFromState = location.state?.selectedUserId;

  useEffect(() => {
    if (selectedUserIdFromState && users.length > 0) {
      const matchedUser = users.find((user) => user._id === selectedUserIdFromState);
      if (matchedUser) {
        setSelectedUser(matchedUser);
      }
    }
  }, [selectedUserIdFromState, users]);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setUserLoading(true);
      const res = await getAllUsers();
      console.log("Full response from getAllUsers:", res);
      setUsers(res.users);
    } catch (error) {
      console.error(
        "Error fetching users:",
        error.response?.data || error.message
      );
      setUsers([]);
    } finally {
      setUserLoading(false);
    }
  };

  // Fetch user-specific data (listings, conversations, reports)
  const fetchUserData = useCallback(async (user) => {
    if (!user) return;

    try {
      setListingLoading(true);
      setConversationLoading(true);
      setReportsLoading(true);

      const [listingsRes, conversationsRes, reportsRes] = await Promise.all([
        getListingsByUser(user._id),
        getUsersConversations(user._id),
        adminApi.getUserSubmittedReports(user._id),
      ]);

      console.log("conversationsRes", conversationsRes);
      console.log("reportsRes", reportsRes);

      setListings(listingsRes.listings || []);
      setConversations(conversationsRes.conversation || []);
      setReports(reportsRes.data?.reports || []);
    } catch (error) {
      console.error(
        "Error fetching user data:",
        error.response?.data || error.message
      );
      setListings([]);
      setConversations([]);
      setReports([]);
    } finally {
      setListingLoading(false);
      setConversationLoading(false);
      setReportsLoading(false);
    }
  }, []);

  // Refresh function for after actions
  const refreshUserData = useCallback(async () => {
    if (!selectedUser) return;

    try {
      // Refresh both the user list (to get updated user status) and user-specific data
      await Promise.all([
        fetchUsers(),
        fetchUserData(selectedUser)
      ]);

      // Update selected user with fresh data
      const updatedUsers = await getAllUsers();
      const refreshedUser = updatedUsers.users.find(u => u._id === selectedUser._id);
      if (refreshedUser) {
        setSelectedUser(refreshedUser);
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  }, [selectedUser, fetchUserData]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch user data when a user is selected
  useEffect(() => {
    fetchUserData(selectedUser);
  }, [selectedUser, fetchUserData]);

  console.log("SET STUFF", listings, conversations, reports);

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || user.status.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleChatClick = (chatId) => {
    navigate("/admin/chat", {
      state: { chatId }, // pass chat ID via state
    });
  };

  if (userLoading) return <p>Loading users...</p>;

  if (selectedUser) {
    return (
      <UserDetail
        setSelectedUser={setSelectedUser}
        selectedUser={selectedUser}
        handleChatClick={handleChatClick}
        userListings={listings}
        listingLoading={listingLoading}
        userConversations={conversations}
        conversationLoading={conversationLoading}
        userReports={reports}
        reportsLoading={reportsLoading}
        refreshUserData={refreshUserData}
      />
    );
  }

  return (
    <UserList
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      filteredUsers={filteredUsers}
      setSelectedUser={setSelectedUser}
      userListings={listings}
    />
  );
};

export default UserManagement;